/**
 * As Ordens de Serviço do próprio credenciado — espelho de
 * `GET /bff/v1/j06-s03-projecao-credenciado/state` (J-06/S03).
 *
 * A rota devolve, em `state.ordens`, a coluna `payload` de
 * `os_projecoes_atores` (audiência `CREDENCIADO`), gravada na ativação da OS
 * (`os_ativar`, migração 0256) e lida por `j06s03_projecao` (0096). O banco
 * recorta pelo vínculo do usuário (0255, política `os_projecoes_recortadas`):
 * a tela não manda identificador nenhum.
 *
 * Os rótulos e formatadores de situação, modalidade e data são os da área do
 * DTP (`operacional/ordens-servico/contrato.ts`): uma OS tem o mesmo nome dos
 * dois lados do balcão.
 */

import type {
  DreResumo,
  EscolaDaOs,
  Modalidade,
  StatusOrdem,
} from '@/modulos/operacional/ordens-servico/contrato';

export {
  ROTULO_DO_RECURSO,
  ROTULO_DO_STATUS,
  VARIANTE_DO_STATUS,
  dataLegivel,
  numeroDaOs,
  rotuloDaModalidade,
  vigenciaLegivel,
} from '@/modulos/operacional/ordens-servico/contrato';
export type {
  DreResumo,
  EscolaDaOs,
  Modalidade,
  StatusOrdem,
} from '@/modulos/operacional/ordens-servico/contrato';

/**
 * O `montagem_snapshot` da OS, copiado para `payload.snapshot` na ativação.
 *
 * Toda função que grava o snapshot põe `termo.id` nele: a montagem antiga
 * (`j06s01_salvar`, 0072/0073) e a emissão da jornada (`os_emitir`, 0254, 0256
 * e 0275:127-139). `recursos`, `dre` e `modalidade` só existem na emissão da
 * jornada; a montagem antiga guarda um recurso só e as escolas com `id`.
 */
export interface SnapshotDaOs {
  termo?: {
    id: string;
    status?: string;
    versao?: number;
    inicio?: string | null;
    fim?: string | null;
  } | null;
  recursos?: {
    veiculo?: { id: string; placa: string | null; crm: string | null } | null;
    condutor?: { id: string; nome: string | null } | null;
    monitor?: { id: string; nome: string | null } | null;
  } | null;
  dre?: DreResumo | null;
  modalidade?: Modalidade | null;
  escolas?: EscolaDaOs[] | null;
}

/**
 * Uma linha de `os_projecoes_atores.payload`, como a API devolve
 * (0256:379-388). A ativação da 0075 grava menos campos — sem `numeroCompleto`,
 * `placa`, `modalidade`, `dre`, `condutor` e `monitor` —, por isso eles são
 * opcionais aqui.
 */
export interface LinhaDaProjecao {
  ativacaoId: string;
  ordemServicoId: string;
  numero: number;
  numeroCompleto?: string | null;
  /** A linha nasce na ativação: hoje, sempre `ATIVA`. */
  status: StatusOrdem;
  revisao?: number;
  inicio: string;
  fim: string;
  reservaId?: string;
  placa?: string | null;
  modalidade?: Modalidade | null;
  dre?: DreResumo | null;
  condutor?: string | null;
  monitor?: string | null;
  snapshot?: SnapshotDaOs | null;
}

/** `GET /bff/v1/j06-s03-projecao-credenciado/state` — só o que a tela lê. */
export interface RespostaDaProjecao {
  state: { ordens?: LinhaDaProjecao[] | null };
}

/**
 * A OS no formato da tela. O que a linha não traz fica `null`, nunca
 * inventado. Sem `ativacaoId` nem `revisao`: são da trilha do DTP, não do
 * credenciado (regra 8 de interface).
 */
export interface OrdemDoCredenciado {
  /** `ordemServicoId` — o endereço do detalhe usa este id. */
  id: string;
  numero: number;
  numeroCompleto: string | null;
  status: StatusOrdem;
  inicio: string;
  fim: string;
  /** `snapshot.termo.id`: o Termo de Adesão sob o qual a OS foi emitida. */
  termoId: string | null;
  placa: string | null;
  crm: string | null;
  modalidade: Modalidade | null;
  dre: DreResumo | null;
  condutor: string | null;
  monitor: string | null;
  escolas: EscolaDaOs[];
}

/**
 * Endereço do detalhe da OS no Portal do Credenciado. A rota nasce na P4
 * (onda 3), que vai junto com esta para DSV; até lá o endereço não tem tela.
 */
export function caminhoDaOrdem(id: string): string {
  return `/credenciado/ordens-de-servico/${encodeURIComponent(id)}`;
}
